/**
 * Theme preference (light / dark / auto), persisted per-device in localStorage.
 *
 * It is a UI preference, not account data - it must apply before the first
 * network call and before paint, so it lives client-side, not on the backend
 * `Me` record. The <html data-theme> attribute is stamped in three places that
 * must stay in agreement:
 *   1. the inline boot script in index.html - pre-paint, no flash;
 *   2. applyTheme() here - when the user changes the setting;
 *   3. watchSystemTheme() here - when the OS flips while the user is on "auto".
 * If you change the storage key or the resolution rule, change the inline
 * script in index.html too.
 */

export type ThemePref = "light" | "dark" | "auto";

const THEME_KEY = "amwai_theme";
const THEMES: ThemePref[] = ["light", "dark", "auto"];

const darkQuery = () => window.matchMedia("(prefers-color-scheme: dark)");

export function getThemePref(): ThemePref {
  const v = localStorage.getItem(THEME_KEY);
  return THEMES.includes(v as ThemePref) ? (v as ThemePref) : "light";
}

/** Resolve "auto" against the OS and stamp <html data-theme="light|dark">. */
export function applyTheme(pref: ThemePref): void {
  const dark = pref === "dark" || (pref === "auto" && darkQuery().matches);
  document.documentElement.dataset.theme = dark ? "dark" : "light";
}

export function setThemePref(pref: ThemePref): void {
  localStorage.setItem(THEME_KEY, pref);
  applyTheme(pref);
}

/** Follow the OS theme live, but only while the user's choice is "auto". */
export function watchSystemTheme(): void {
  darkQuery().addEventListener("change", () => {
    if (getThemePref() === "auto") applyTheme("auto");
  });
}
